import AddIcon from "@mui/icons-material/Add";
import MagicIcon from "@mui/icons-material/AutoAwesome";
import MicIcon from "@mui/icons-material/Mic";
import SendIcon from "@mui/icons-material/Send";
import {
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    ListItemIcon,
    MenuItem,
    MenuList,
    Popover,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
/* Upload package */
import Uploady, { Destination } from '@rpldy/uploady'
import UploadDropZone from '@rpldy/upload-drop-zone'
import { asUploadButton } from '@rpldy/upload-button'
/* Networking */
import { ApiService } from "../../services";
import { DocumentService } from "../../services/documentService";
import { useMutation, useQuery } from "@tanstack/react-query";
/* Global Settings */
import { useConfig } from "../../config/ConfigProvider";
import { useAppStateContext } from "../../state/AppContextProvider";
/* Models */
import { AttachmentRequest, DocumentItem } from "../../common/document.model";
import { UploadingStatus } from "../../common/models";

/* Components/Icons */
import { Attachment, Folder, Image } from "@mui/icons-material";
import { useState, useEffect, useCallback, forwardRef, useContext } from "react";
import uuid from "react-uuid";
import { useSpeechToText } from "../../utils/speechToText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrophone } from "@fortawesome/free-solid-svg-icons";
import { HttpServiceContext } from "../../state/HttpProvider";

interface Props {
    onSend: (question: string, id?: string) => void
    disabled: boolean
    clearOnSend?: boolean
    conversationId?: string
    initialValue?: string
    resetInput?: boolean
}

export const MuiQuestionInput = ({
    onSend,
    disabled,
    clearOnSend,
    conversationId,
    initialValue,
    resetInput
}: Props) => {
    const appStateContext = useAppStateContext()

    const [question, setQuestion] = useState<string>(initialValue || '')
    const [error, setError] = useState<string | null>(null)
    const [filesToUpload, setFilesToUpload] = useState<File[]>([])
    const config = useConfig();
    const speechToText = useSpeechToText()
    // Use app state for phone detection (includes Chrome extension)
    const isPhone = appStateContext.state.isPhone || appStateContext.state.isChromeExtension;
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    // Method to add files programmatically (for scraped content)
    const addFileToUpload = useCallback((file: File) => {
        // Check file size
        const DOCUPLOAD_MAX_SIZE_MB = appStateContext?.state.frontendSettings?.upload_max_filesize;
        if (DOCUPLOAD_MAX_SIZE_MB && file.size > DOCUPLOAD_MAX_SIZE_MB * 1024 * 1024) {
            setError('File size too large. Maximum file size is ' + DOCUPLOAD_MAX_SIZE_MB + 'MB.');
            return false;
        }
        
        // Check file limit - allow up to 5 files
        if (filesToUpload.length >= 5) {
            setError('File upload limit reached. Maximum 5 files per message.');
            return false;
        }
        
        // Add file to upload queue
        appStateContext?.dispatch({
            type: 'SET_UPLOADING_STATUS',
            payload: UploadingStatus.STANDBY
        });
        setFilesToUpload((curr) => [...curr, file]);
        setError(''); // Clear any previous errors
        return true;
    }, [filesToUpload.length, appStateContext]);

    const sendQuestionDisabled = disabled || !question?.trim()

    // Only clear files when starting a completely new conversation (sessionId becomes null)
    useEffect(() => {
        return () => {
            if (appStateContext?.state.currentConversation.sessionId === null) {
                setFilesToUpload([])
                setQuestion('')
                setError(null)
            }
        }
    }, [appStateContext?.state.currentConversation.sessionId])

    useEffect(() => {
        if (speechToText.transcript && speechToText.isListening) {
            setQuestion(speechToText.transcript)
        }
    }, [speechToText.transcript, speechToText.isListening])

    // Listen for Chrome extension scraped content
    useEffect(() => {
        const processedContent = new Set<string>(); // Track processed content by URL+timestamp
        
        const handleChromeMessage = (event: MessageEvent) => {
            // Validate origin for security
            const allowedOrigins = ['chrome-extension://', 'moz-extension://'];
            const isValidOrigin = allowedOrigins.some(origin => event.origin.startsWith(origin)) || 
                                event.origin === window.location.origin;
            
            if (!isValidOrigin) return;

            if (event.data?.type === 'PAGE_CONTENT_SCRAPED' && event.data?.data) {
                const contentData = event.data.data;
                
                // Create a unique identifier for this content
                const contentId = `${contentData.url}_${contentData.timestamp || Date.now()}`;
                
                // Check if we've already processed this content
                if (processedContent.has(contentId)) {
                    console.log('⚠️ Duplicate scraped content ignored:', contentData.title);
                    return;
                }
                
                // Mark this content as processed
                processedContent.add(contentId);
                
                console.log('MuiQuestionInput received scraped content:', contentData);
                
                // Import ChromeExtensionHandler dynamically to avoid circular imports
                import('../../utils/chromeExtensionHandler').then(({ default: ChromeExtensionHandler }) => {
                    try {
                        const file = ChromeExtensionHandler.createFileFromScrapedContent(contentData);
                        const success = addFileToUpload(file);
                        
                        if (success) {
                            console.log('✅ Scraped content added to upload queue:', file.name);
                            // Optionally add a message to indicate the file was added
                            setQuestion(prev => prev || `Analyze the scraped content from "${contentData.title}"`);
                        }
                    } catch (error) {
                        console.error('❌ Failed to process scraped content:', error);
                        setError('Failed to process scraped content');
                        // Remove from processed set if there was an error so it can be retried
                        processedContent.delete(contentId);
                    }
                });
            }
        };

        window.addEventListener('message', handleChromeMessage);
        return () => window.removeEventListener('message', handleChromeMessage);
    }, []); // Remove addFileToUpload from dependencies to prevent re-registering

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        if (resetInput) {
            setQuestion('')
        }
    }, [resetInput])

    useEffect(() => {
        if (!appStateContext?.state.currentConversation.sessionId) {
            setQuestion('')
        }
    }, [appStateContext?.state.currentConversation.sessionId])

    const activeDocumentId = appStateContext?.state.activeDocument?.documentId

    const { httpService: apiService } = useContext(HttpServiceContext)
    const documentService = new DocumentService(apiService);
    const uploadFile = useMutation({
        mutationFn: async (file: File[]) => {
            return documentService.uploadDocuments(file, new AbortController().signal)
        }
    })

    const saveAttachment = useMutation({
        mutationFn: async (payload: AttachmentRequest) => {
            return documentService.uploadAttachmentToServer(payload, new AbortController().signal)
        }
    })

    const { data: _data, refetch } = useQuery({
        queryKey: ['uploadStatus', activeDocumentId],
        queryFn: async () => {
            const data = await documentService.getDocumentStatus(activeDocumentId!, new AbortController().signal)

            if (data.completed) {
                appStateContext.dispatch({
                    type: 'SET_UPLOADING_STATUS',
                    payload: UploadingStatus.COMPLETED
                })
            } else {
                refetch()
            }

            return data
        },
        enabled: false, // Disabled since we're using the timeout fallback
        refetchInterval: 500,
        gcTime: 0,
    })

    const DOCUPLOAD_MAX_SIZE_MB = appStateContext?.state.frontendSettings?.upload_max_filesize

    const sendQuestion = useCallback(async () => {
        if (appStateContext?.state.uploadingStatus === UploadingStatus.UPLOADING || appStateContext.state.isLoading) {
            return
        }

        /**
         * If there is no session id, create a new one.
         * Only happens on the first message sent in a new chat.
         */
        let sessionId = appStateContext?.state.currentConversation.sessionId
        if (!sessionId) {
            sessionId = uuid()
            await appStateContext.dispatch({
                type: 'SET_CURRENT_CONVERSATION',
                payload: {
                    sessionId: sessionId,
                    messages: [],
                    attachments: appStateContext.state.currentConversation.attachments // Preserve existing attachments
                }
            })
        }

        if (filesToUpload.length > 0) {
            console.log('Starting file upload for files:', filesToUpload.map(f => f.name))
            
            appStateContext.dispatch({
                type: 'SET_UPLOADING_STATUS',
                payload: UploadingStatus.UPLOADING
            })

            uploadFile.mutate(filesToUpload, {
                onSuccess: async (response) => {
                    console.log('File upload successful, response:', response)
                    
                    console.log('Setting upload status to ANALYZING')
                    appStateContext.dispatch({
                        type: 'SET_UPLOADING_STATUS',
                        payload: UploadingStatus.ANALYZING
                    })

                    try {
                        console.log('Saving attachment to server')
                        await saveAttachment.mutate({
                            sessionId: sessionId as string,
                            documentId: response.documentId,
                            documentName: filesToUpload.map(f => f.name) // <-- store all names
                        })

                        console.log('Attachment saved successfully')

                        // Create separate attachments for each file
                        const newAttachments = filesToUpload.map(file => ({
                            documentId: response.documentId, // Same documentId for all files in this upload
                            documentName: [file.name] // Each attachment has its own file name
                        }));

                        // Update both the current conversation AND the chat history
                        const currentAttachments = [...appStateContext.state.currentConversation.attachments, ...newAttachments];
                        
                        // Update current conversation
                        appStateContext.dispatch({
                            type: 'SET_CURRENT_CONVERSATION',
                            payload: {
                                sessionId: sessionId as string,
                                messages: appStateContext.state.currentConversation.messages,
                                attachments: currentAttachments
                            }
                        });

                        // Update chat history to persist the attachment
                        const currentChatHistory = appStateContext.state.chatHistory || { chatHistory: {}, attachments: {} };
                        const updatedChatHistory = {
                            ...currentChatHistory,
                            attachments: {
                                ...currentChatHistory.attachments,
                                [sessionId as string]: currentAttachments
                            }
                        };
                        
                        appStateContext.dispatch({
                            type: 'SET_CHAT_HISTORY',
                            payload: updatedChatHistory
                        });

                        // Set active document AFTER conversation update to ensure it doesn't get overridden
                        // Use the first attachment as the active document
                        appStateContext.dispatch({
                            type: 'SET_ACTIVE_DOCUMENT',
                            payload: newAttachments[0]
                        });

                        // Clear the files - the query will handle setting COMPLETED status
                        setFilesToUpload(() => [])

                        // Fallback: if polling doesn't work, set to completed after 3 seconds
                        setTimeout(() => {
                            console.log('Setting upload status to COMPLETED')
                            appStateContext.dispatch({
                                type: 'SET_UPLOADING_STATUS',
                                payload: UploadingStatus.COMPLETED
                            });
                        }, 1000); // Reduced to 1 second since processing seems instant
                    } catch (error) {
                        console.error('Error saving attachment:', error);
                        appStateContext.dispatch({
                            type: 'SET_UPLOADING_STATUS',
                            payload: UploadingStatus.FAILED
                        });
                        setError('Error saving attachment');
                    }
                },
                onError: (error) => {
                    console.error('File upload failed:', error)
                    appStateContext.dispatch({
                        type: 'SET_UPLOADING_STATUS',
                        payload: UploadingStatus.FAILED
                    })
                    console.error('Error uploading file', error)
                    setError('Error uploading file')
                }
            })
            return // Do not send the question if there are files to upload
        }
        if (sendQuestionDisabled) {
            return
        }

        let questionValue = question

        if (clearOnSend) {
            setQuestion('')
        }

        sessionId ? onSend(questionValue, sessionId) : onSend(questionValue)
        setError('')
    }, [
        sendQuestionDisabled,
        question,
        clearOnSend,
        conversationId,
        onSend,
        filesToUpload.length,
        appStateContext.state.isLoading,
        appStateContext.state.uploadingStatus
    ])

    useEffect(() => {
        if (appStateContext?.state.uploadingStatus === UploadingStatus.COMPLETED) {
            if (filesToUpload.length > 0) {
                setFilesToUpload(() => [])
            } else {
                sendQuestion()
            }
        }
    }, [filesToUpload.length, appStateContext?.state.uploadingStatus])

    const onEnterPress = useCallback(
        (ev: React.KeyboardEvent<Element>) => {
            if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
                ev.preventDefault()
                sendQuestion()
            }
        },
        [sendQuestion]
    )

    const checkFileType = (): string => {
        /**
         * https://learn.microsoft.com/en-us/azure/search/search-blob-metadata-properties
         * 
         * CSV (see Indexing CSV blobs)
            EML
            EPUB
            GZ
            HTML
            JSON (see Indexing JSON blobs)
            KML (XML for geographic representations)
            Microsoft Office formats: DOCX/DOC/DOCM, XLSX/XLS/XLSM, PPTX/PPT/PPTM, MSG (Outlook emails), XML (both 2003 and 2006 WORD XML)
            Open Document formats: ODT, ODS, ODP
            PDF
            Plain text files (see also Indexing plain text)
            RTF
            XML
            ZIP
         */
        const allowedTypes = [
            'text/csv',
            'message/rfc822',
            'application/epub+zip',
            'application/gzip',
            'text/html',
            'application/json',
            'application/vnd.google-earth.kml+xml',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'application/vnd.ms-outlook',
            'application/xml',
            'application/vnd.oasis.opendocument.text',
            'application/vnd.oasis.opendocument.spreadsheet',
            'application/vnd.oasis.opendocument.presentation',
            'application/pdf',
            'text/plain',
            'application/rtf',
            'application/xml',
            'application/zip'
        ]

        return allowedTypes.join(',')
    }

    const fileSizeOk = useCallback(async (file: any) => {
        setError('')
        handleClose()

        if (DOCUPLOAD_MAX_SIZE_MB && file.size > DOCUPLOAD_MAX_SIZE_MB * 1024 * 1024) {
            setError('File size too large. Maximum file size is ' + DOCUPLOAD_MAX_SIZE_MB + 'MB.')
            return false
        } else if (filesToUpload.length >= 5) {
            setError('File upload limit reached. Maximum 5 files per message.')
            return false
        } else {
            await appStateContext?.dispatch({
                type: 'SET_UPLOADING_STATUS',
                payload: UploadingStatus.STANDBY
            })
            setFilesToUpload((curr) => [...curr, file])
            return true
        }
    }, [filesToUpload.length])

    useEffect(() => {
        setQuestion(initialValue || '')
        // Focus the TextField after the initial value is set
        const textarea = document.querySelector<HTMLTextAreaElement>(
            'textarea.ms-TextField-field.ms-TextField--unresizable'
        )
        if (textarea) {
            textarea.focus()
        }
    }, [initialValue])

    const onQuestionChange = useCallback(
        (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            // setQuestion(newValue || '')
            setQuestion((_ev.target as HTMLTextAreaElement).value || newValue || '')
        },
        []
    )

    const uploadDestination: Destination = {
        url: '/document/upload'
    }

    const UploadDocument = asUploadButton(
        forwardRef<HTMLDivElement>((props, ref) => (
            <MenuItem {...props}>
                <ListItemIcon>
                    <Attachment
                        sx={{
                            transform: "scale(-1, 1) rotate(45deg)",
                        }}
                    />
                </ListItemIcon>
                <Typography variant="inherit">Upload File</Typography>
            </MenuItem>
        ))
    )

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const processing = (appStateContext?.state.uploadingStatus === UploadingStatus.UPLOADING || appStateContext.state.isLoading)

    return (
        <>
            <Stack
                direction={"row"}
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: 'wrap', gap: 1 }}
            >
                {/* Show "Selected File(s):" label only if there are attachments */}
                {(appStateContext?.state.currentConversation.attachments?.length > 0 || (!processing && filesToUpload.length > 0)) && (
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                            color: "primary.main",
                            fontSize: "14px",
                            whiteSpace: "nowrap"
                        }}
                    >
                        Selected File(s):
                    </Typography>
                )}
                
                {/* Display persisted attachments from conversation state */}
                {appStateContext?.state.currentConversation.attachments?.map((attachment, index) => {
                    return (
                        <Chip
                            key={`attachment-${attachment.documentId}-${index}`}
                            label={Array.isArray(attachment.documentName) 
                                ? attachment.documentName.join(', ') 
                                : attachment.documentName}
                            icon={<Folder />}
                            variant="outlined"
                            color="primary"
                            onDelete={() => {
                                // Remove attachment from current conversation
                                const updatedAttachments = appStateContext.state.currentConversation.attachments.filter((_, i) => i !== index);
                                
                                appStateContext.dispatch({
                                    type: 'SET_CURRENT_CONVERSATION',
                                    payload: {
                                        sessionId: appStateContext.state.currentConversation.sessionId!,
                                        messages: appStateContext.state.currentConversation.messages,
                                        attachments: updatedAttachments
                                    }
                                });

                                // Update chat history to persist the removal
                                const currentChatHistory = appStateContext.state.chatHistory || { chatHistory: {}, attachments: {} };
                                const updatedChatHistory = {
                                    ...currentChatHistory,
                                    attachments: {
                                        ...currentChatHistory.attachments,
                                        [appStateContext.state.currentConversation.sessionId!]: updatedAttachments
                                    }
                                };
                                
                                appStateContext.dispatch({
                                    type: 'SET_CHAT_HISTORY',
                                    payload: updatedChatHistory
                                });

                                // Clear active document if this was the active one
                                if (appStateContext.state.activeDocument?.documentId === attachment.documentId) {
                                    appStateContext.dispatch({
                                        type: 'SET_ACTIVE_DOCUMENT',
                                        payload: updatedAttachments[0] || null
                                    });
                                }
                            }}
                            sx={{ 
                                '& .MuiChip-deleteIcon': {
                                    fontSize: '18px'
                                }
                            }}
                        />
                    )
                })}
                
                {/* Display temporary files pending upload */}
                {!processing && filesToUpload.map((file, index) => {
                    return (
                        <Chip
                            key={`upload-${file.name}-${index}`}
                            onDelete={() => {
                                setFilesToUpload((curr) => curr.filter((_f, i) => index !== i))
                                setError('')
                            }}
                            label={file.name}
                            icon={<Folder />}
                            variant="filled"
                            color="secondary"
                            sx={{ 
                                '& .MuiChip-deleteIcon': {
                                    fontSize: '18px'
                                }
                            }}
                        />
                    )
                })}
                
                {error && (
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: 'error.main', 
                            fontWeight: 500,
                            fontSize: '14px'
                        }}
                    >
                        {error}
                    </Typography>
                )}
            </Stack>
            <Uploady
                autoUpload={false}
                destination={uploadDestination}
                fileFilter={fileSizeOk}
                params={{ conversationId: conversationId }}
                accept={checkFileType()}
            >
                <UploadDropZone onDragOverClassName="drag-over">
                    {/* Input Section */}
                    <TextField
                        fullWidth
                        placeholder="You can respond here"
                        variant="outlined"
                        id="chat_input"
                        multiline
                        minRows={1}
                        maxRows={5}
                        sx={{
                            bgcolor: "background.paper",
                            borderRadius: "16px",
                            "& fieldset": {
                                borderRadius: "16px",
                                border: '1px solid',
                                borderColor: "custom.brandPrimary !important",
                                padding: "5px 5px 5px 2px", // top right bottom left
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: "5px 5px 5px 2px", // top right bottom left
                            },
                            "& .MuiOutlinedInput-inputMultiline": {
                                padding: "5px 5px 5px 2px", // top right bottom left
                            },
                        }}
                        disabled={processing || speechToText.isListening}
                        onKeyDown={onEnterPress}
                        value={question}
                        onChange={onQuestionChange}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <>
                                        <InputAdornment position="start">
                                            <IconButton
                                                onClick={handleClick}
                                                aria-describedby={id}
                                                disabled={processing || speechToText.isListening}
                                                sx={{
                                                    color: "custom.placeholderGray",
                                                }}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    </>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <IconButton
                                                disabled={processing}
                                                onClick={speechToText.startListening}
                                                sx={{
                                                    color: "custom.placeholderGray",
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faMicrophone} beat={speechToText.isListening} color={!speechToText.isListening ? '' : '#e50c20'} />
                                            </IconButton>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    background: 'background.paper',
                                                    borderRadius: '40px',
                                                    boxShadow: 'none',
                                                    height: '32px',
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    cursor: 'default',
                                                    px: 0,
                                                    py: 0,
                                                    border: '1.5px solid #e50c20',
                                                    transition: 'background 0.2s, color 0.2s',
                                                    opacity: processing || sendQuestionDisabled || speechToText.isListening ? 0.7 : 1,
                                                    ...(!(processing || sendQuestionDisabled || speechToText.isListening) && {
                                                        '&:hover': {
                                                            bgcolor: '#e50c20',
                                                            '& .generate-btn-text': {
                                                                color: '#fff',
                                                            },
                                                        },
                                                    }),
                                                }}
                                                onClick={() => {
                                                    if (!(processing || sendQuestionDisabled || speechToText.isListening)) sendQuestion();
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        background: '#e50c20',
                                                        width: '2%',
                                                        minWidth: '20px',
                                                        height: '100%',
                                                        borderRadius: '40px 0 0 40px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                />
                                                <Typography
                                                    className="generate-btn-text"
                                                    component="span"
                                                    sx={{
                                                        fontSize: '.95rem',
                                                        fontWeight: 600,
                                                        pl: 2,
                                                        pr: 3,
                                                        py: 1.5,
                                                        textAlign: 'left',
                                                        whiteSpace: 'nowrap',
                                                        color: 'text.primary',
                                                        // REMOVE the line below so hover works:
                                                        // bgcolor: 'background.paper',
                                                        userSelect: 'none',
                                                        transition: 'color 0.2s',
                                                    }}
                                                >
                                                    {isPhone ? <SendIcon sx={{ fontSize: 22, color: "text.primary" }} /> : "Generate"}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </InputAdornment>
                                ),
                            }
                        }}
                    />
                </UploadDropZone>
                <Popover
                    id='upload-popover'
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}

                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                >
                    <MenuList>
                        <MenuItem>
                            <ListItemIcon>
                                <Image />
                            </ListItemIcon>
                            <Typography variant="inherit">Upload Image</Typography>
                        </MenuItem>
                        <UploadDocument />
                    </MenuList>
                </Popover>
            </Uploady>

            {/* Disclaimer section */}
            <Stack
                direction={isPhone ? "column" : "row"}
                spacing={1}
                justifyContent="center"
                alignItems="center"
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        color: "grey.500",
                        fontSize: isPhone ? "11px" : "14px",
                        textAlign: isPhone ? "center" : "left",
                    }}
                >
                    This agent is secure and exclusively accesses data within its
                    designated organization.
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        fontSize: isPhone ? "11px" : "14px",
                        background:
                            "linear-gradient(45deg, #e50c20, #b8091a)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        color: "transparent",
                        textAlign: isPhone ? "center" : "left",
                    }}
                >
                    Always verify critical details.
                </Typography>
            </Stack>
        </>
    );
};
